import { useFormaliteService } from '@europrocurement/l2d-domain';
import React, { createContext, PropsWithChildren, useContext, useEffect, useReducer } from 'react';
import { DataProviderProps, DataState } from './types';

export type DossierValidatorStatus = {
    id: number;
    label: string;
    code: string;
    sortOrder: number;
    statusFlag: number;
};

type DossierValidator = {
    id: number;
    label: string;
    typeId: number;
    typeLabel: string;
    dossierStatuses: Array<unknown>;
    statuses: Array<DossierValidatorStatus>;
};

type DossierValidatorState = DataState & {
    data: Array<DossierValidator>;
};

const INIT_ITEMS_CTX = 'init_dossier_validators_ctx';

type Action = { type: 'init_dossier_validators_ctx'; payload: Array<DossierValidator> };

export const DossierValidatorsContext = createContext<DossierValidatorState | null>(null);
export const DossierValidatorsDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function dossierValidatorsReducer(
    state: DossierValidatorState,
    action: Action,
): DossierValidatorState {
    switch (action.type) {
        case INIT_ITEMS_CTX: {
            return {
                loaded: true,
                data: [...action.payload],
            };
        }
        default: {
            throw new Error(`Unknown action: ${action.type}`);
        }
    }
}

export const DossierValidatorsProvider: React.FunctionComponent<
    PropsWithChildren<DataProviderProps>
> = function ({ autoload, children }) {
    const [state, dispatch] = useReducer(dossierValidatorsReducer, {
        loaded: false,
        data: [],
    });
    const { validatorsApi } = useFormaliteService();

    useEffect(() => {
        if (!autoload) {
            return;
        }

        validatorsApi
            .apiDossiersValideursGetCollection({ itemsPerPage: 999 })
            .then((response) => {
                dispatch({
                    type: INIT_ITEMS_CTX,
                    payload: response.data['hydra:member'].map((item) => ({
                        id: item.id as unknown as number,
                        label: item.label as string,
                        typeId: item.typeId as unknown as number,
                        typeLabel: item.typeLabel as string,
                        dossierStatuses: item.dossierStatuses as Array<unknown> as Array<number>,
                        statuses: item.statuses as Array<DossierValidatorStatus>,
                    })),
                });
            })
            .catch((e) => console.error(e));
    }, [validatorsApi, autoload]);

    return (
        <DossierValidatorsContext.Provider value={state}>
            <DossierValidatorsDispatchContext.Provider value={dispatch}>
                {children}
            </DossierValidatorsDispatchContext.Provider>
        </DossierValidatorsContext.Provider>
    );
};

export function useDossierValidatorsCtx() {
    const state = useContext(DossierValidatorsContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getDossierValidators = (): Array<DossierValidator> => state?.data || [];

    const getDossierValidatorById = (id: number): DossierValidator | undefined =>
        (state?.data || []).find((item) => item.id === id);

    const getDossierValidatorsFiltered = (
        filters: Partial<Record<keyof DossierValidator, unknown>>,
    ): Array<DossierValidator> =>
        (state?.data || []).filter((item) =>
            Object.entries(filters).every(
                ([key, value]) => item[key as keyof DossierValidator] === value,
            ),
        );

    const getDossierValidatorFiltered = (
        filters: Partial<Record<keyof DossierValidator, unknown>>,
    ): DossierValidator | undefined => getDossierValidatorsFiltered(filters)[0] || undefined;

    return {
        isDataLoaded,
        getDossierValidators,
        getDossierValidatorById,
        getDossierValidatorsFiltered,
        getDossierValidatorFiltered,
    };
}

export function useDossierValidatorCtxDispatch() {
    return useContext(DossierValidatorsDispatchContext);
}
